import { useMemo } from 'react';
import type { NpsAggregateResult } from '@/types';
import './NpsGauge.css';

interface NpsGaugeProps {
  aggregate: NpsAggregateResult;
}

function npsColor(score: number): string {
  if (score < 0) return 'var(--bp-error, #ef4444)';
  if (score < 50) return 'var(--bp-warning, #f59e0b)';
  return 'var(--bp-success, #22c55e)';
}

function npsVerdict(score: number): string {
  if (score >= 50) return 'Excellent';
  if (score >= 0) return 'Good';
  return 'Needs attention';
}

export function NpsGauge({ aggregate }: NpsGaugeProps) {
  const { score, promoters, passives, detractors, total } = aggregate;

  const pct = useMemo(() => {
    if (total === 0) return { promoters: 0, passives: 0, detractors: 0 };
    return {
      promoters: Math.round((promoters / total) * 100),
      passives: Math.round((passives / total) * 100),
      detractors: 100 - Math.round((promoters / total) * 100) - Math.round((passives / total) * 100),
    };
  }, [promoters, passives, detractors, total]);

  const color = npsColor(score);

  // Circular progress ring
  const size = 120;
  const stroke = 8;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const normalizedScore = Math.max(0, Math.min(100, ((score + 100) / 200) * 100));
  const dashOffset = circumference - (circumference * normalizedScore) / 100;

  return (
    <div className="nps-gauge" aria-label={`NPS score: ${score}`}>
      {/* Ring */}
      <div className="nps-gauge__ring-wrap">
        <svg viewBox={`0 0 ${size} ${size}`} className="nps-gauge__ring">
          {/* Track */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="rgba(255,255,255,0.06)"
            strokeWidth={stroke}
          />
          {/* Fill */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            className="nps-gauge__ring-fill"
          />
        </svg>
        <div className="nps-gauge__ring-center">
          <span className="nps-gauge__score" style={{ color }}>{score}</span>
          <span className="nps-gauge__verdict">{npsVerdict(score)}</span>
        </div>
      </div>

      {/* Stacked bar */}
      <div className="nps-gauge__bar-wrap">
        <div className="nps-gauge__bar">
          {pct.promoters > 0 && (
            <div
              className="nps-gauge__bar-seg nps-gauge__bar-seg--promoter"
              style={{ width: `${pct.promoters}%` }}
            />
          )}
          {pct.passives > 0 && (
            <div
              className="nps-gauge__bar-seg nps-gauge__bar-seg--passive"
              style={{ width: `${pct.passives}%` }}
            />
          )}
          {pct.detractors > 0 && (
            <div
              className="nps-gauge__bar-seg nps-gauge__bar-seg--detractor"
              style={{ width: `${pct.detractors}%` }}
            />
          )}
        </div>
        <div className="nps-gauge__bar-labels">
          <span className="nps-gauge__bar-label">
            <span className="nps-gauge__bar-dot nps-gauge__bar-dot--promoter" />
            {pct.promoters}%
          </span>
          <span className="nps-gauge__bar-label">
            <span className="nps-gauge__bar-dot nps-gauge__bar-dot--passive" />
            {pct.passives}%
          </span>
          <span className="nps-gauge__bar-label">
            <span className="nps-gauge__bar-dot nps-gauge__bar-dot--detractor" />
            {pct.detractors}%
          </span>
        </div>
      </div>

      {/* Meta row */}
      <div className="nps-gauge__meta">
        <span>{total} rating{total !== 1 ? 's' : ''}</span>
        <span className="nps-gauge__meta-sep">·</span>
        <span>Score range −100 to +100</span>
      </div>
    </div>
  );
}
